import React, { useEffect, useRef, useState, useCallback } from 'react';
import './SigningStatus.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageLayout from './common/PageLayout';
import {
    getAuthSession,
    saveAuthSession,
    syncActiveAuthorization,
    nextUnsignedIndex,
    isTerminalFailure,
    getConsentStatus,
    startOrchestration,
    buildAuthSession,
    initiateAuthorization,
} from '../utils/authSession';

const POLL_INTERVAL_MS = 2000;

// Step 3 — the dual-signing coordinator. It is the hub the flow returns to after each
// authoriser signs. It prompts the next in-browser authoriser, shows the state of emailed
// (out-of-band) authorisers, polls the overall consent once everyone in-browser is done,
// and restarts orchestration if signing fails.
function SigningStatus() {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [session, setSession] = useState(() => getAuthSession());
    const [restarting, setRestarting] = useState(false);
    const [resendingId, setResendingId] = useState(null);
    const [resendMessage, setResendMessage] = useState(null);
    const pollTimerRef = useRef(null);
    const isPollingRef = useRef(false);
    const restartingRef = useRef(false);

    useEffect(() => {
        if (!session) {
            navigate('/aspspsList');
        }
    }, [session, navigate]);

    const list = session?.list || [];
    const nextIdx = session ? nextUnsignedIndex(session) : -1;
    const allInBrowserDone = nextIdx === -1;
    const anyFailed = list.some((el) => isTerminalFailure(el.scaStatus));

    const stopPolling = useCallback(() => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        isPollingRef.current = false;
    }, []);

    // Failure -> re-run StartConsentOrchestration (force:false). The backend reuses the
    // consent + re-issues authorisation(s), or hands back a brand-new consent. Only fall
    // all the way back to bank selection if orchestration itself errors.
    const restartOnFailure = useCallback(async () => {
        if (restartingRef.current) return;
        restartingRef.current = true;
        stopPolling();
        setRestarting(true);

        const current = getAuthSession();
        if (!current?.createInputs?.bicFi) {
            navigate('/aspspsList');
            return;
        }
        try {
            const responseArray = await startOrchestration({ createInputs: current.createInputs, force: false });
            if (!responseArray || responseArray.length === 0) {
                // Nothing left to sign — the consent is already usable.
                navigate('/pick-account');
                return;
            }
            const rebuilt = buildAuthSession(responseArray, current.createInputs, { isDual: current.isDual });
            restartingRef.current = false;
            setRestarting(false);
            setSession(rebuilt);
        } catch (err) {
            console.error('Error restarting consent orchestration:', err);
            navigate('/aspspsList');
        }
    }, [navigate, stopPolling]);

    // Poll the overall consent once every in-browser authoriser is done (we may still be
    // waiting on emailed authorisers, or on the bank to finalise the consent).
    useEffect(() => {
        if (!session) return undefined;
        if (anyFailed) {
            restartOnFailure();
            return undefined;
        }
        if (!allInBrowserDone) return undefined;

        isPollingRef.current = true;

        const poll = async () => {
            if (!isPollingRef.current) return;
            try {
                const status = await getConsentStatus({
                    consentId: session.consentId,
                    createInputs: session.createInputs,
                });
                if (!isPollingRef.current) return;
                if (status === 'valid') {
                    stopPolling();
                    navigate('/pick-account');
                    return;
                }
                if (isTerminalFailure(status)) {
                    stopPolling();
                    restartOnFailure();
                    return;
                }
            } catch (err) {
                // transient — keep polling
            }
            if (isPollingRef.current) {
                pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
            }
        };
        poll();

        return stopPolling;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allInBrowserDone, anyFailed, session]);

    // Advance to the next unsigned in-browser authoriser and send them to method selection.
    const handleContinue = () => {
        const current = getAuthSession();
        if (!current) {
            navigate('/aspspsList');
            return;
        }
        const idx = nextUnsignedIndex(current);
        if (idx === -1) return;
        current.currentIndex = idx;
        saveAuthSession(current);
        syncActiveAuthorization(current);
        navigate('/select-method');
    };

    const handleResendLink = useCallback(async (authorizationId) => {
        const current = getAuthSession();
        if (!current) return;

        setResendingId(authorizationId);
        setResendMessage(null);

        try {
            const result = await initiateAuthorization({
                consentId: current.consentId,
                authorizationIds: [authorizationId],
                authMethod: 'email',
                createInputs: current.createInputs,
                force: true,
            });

            if (result && result.length > 0) {
                setResendMessage({ type: 'success', text: t('signStatusLinkResent', 'Link resent successfully') });
                setTimeout(() => setResendMessage(null), 3000);
            } else {
                setResendMessage({ type: 'error', text: t('signStatusResendFailed', 'Failed to resend link') });
            }
        } catch (err) {
            console.error('Error resending link:', err);
            setResendMessage({ type: 'error', text: t('signStatusResendError', 'Error resending link') });
        } finally {
            setResendingId(null);
        }
    }, [t]);

    const authoriserState = (el, idx) => {
        if (isTerminalFailure(el.scaStatus)) return t('signStatusFailed', 'Failed');
        if (String(el.scaStatus).toLowerCase() === 'finalised') return t('signStatusSigned', 'Signed');
        if (el.emailNotification || el.emailChosen) return t('signStatusEmailed', 'Link sent — awaiting signature');
        if (idx === nextIdx) return t('signStatusNext', 'Ready to sign');
        return t('signStatusWaiting', 'Waiting');
    };

    if (!session) {
        return null;
    }

    const navigationProps = { previousPath: '/aspspsList', showNext: false };
    const showContinue = !allInBrowserDone && !anyFailed && !restarting;

    return (
        <PageLayout currentStep={3} navigationProps={navigationProps} infoText="signStatusInfoText">
            <div className="signing-status-content">
                <h1 className="main-title">{t('signStatusTitle', 'Signing status')}</h1>
                <p className="main-subtitle">{t('signStatusSubtitle', 'This consent requires more than one authoriser.')}</p>

                <ul className="signing-status-list">
                    {list.map((el, idx) => (
                        <li
                            key={el.authorizationId || idx}
                            className={`signing-status-item ${String(el.scaStatus).toLowerCase() === 'finalised' ? 'signed' : ''}`}
                        >
                            <span className="authoriser-id">
                                {el.authoriserId || `${t('signStatusAuthoriser', 'Authoriser')} ${idx + 1}`}
                            </span>
                            <div className="authoriser-status-actions">
                                <span className="authoriser-state">{authoriserState(el, idx)}</span>
                                {(el.emailNotification || el.emailChosen) && String(el.scaStatus).toLowerCase() !== 'finalised' && (
                                    <button
                                        type="button"
                                        className="resend-link-btn"
                                        onClick={() => handleResendLink(el.authorizationId)}
                                        disabled={resendingId !== null}
                                        title={t('signStatusResendTooltip', 'Resend authorization link')}
                                    >
                                        {resendingId === el.authorizationId ? (
                                            t('signStatusResending', 'Resending…')
                                        ) : (
                                            t('signStatusResend', 'Resend link')
                                        )}
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>

                {resendMessage && (
                    <div className={`resend-message resend-message-${resendMessage.type}`}>
                        {resendMessage.text}
                    </div>
                )}

                {showContinue && (
                    <button type="button" className="signing-continue-btn" onClick={handleContinue}>
                        {t('signStatusContinue', 'Continue')}
                    </button>
                )}

                {allInBrowserDone && !anyFailed && !restarting && (
                    <div className="signing-awaiting">{t('signStatusAwaiting', 'Waiting for all signatures to complete…')}</div>
                )}

                {restarting && (
                    <div className="signing-awaiting">{t('signStatusRestarting', 'Restarting — please wait…')}</div>
                )}
            </div>
        </PageLayout>
    );
}

export default SigningStatus;
